import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  ArrowLeftRight,
  BookOpen,
  Check,
  FileText,
  Lightbulb,
  Link2,
  RotateCcw,
  Scissors,
  Search,
  Undo2,
  X,
} from 'lucide-react'
import { CounterpartEditor } from './CounterpartEditor'
import { formatCurrency } from './format'
import type { APXFieldValue, MatchState, Transaction } from './types'
import {
  useCommitMatches,
  useConfirmMatch,
  useReverseMatch,
} from './hooks/useReconApi'
import { CommitDialog } from './CommitDialog'
import { LinkTab } from './LinkTab'
import { MatchTab } from './MatchTab'
import { PostTab } from './PostTab'
import { SplitTab } from './SplitTab'
import { TransferTab } from './TransferTab'

interface TransactionRowExpandedProps {
  transaction: Transaction
  clientId: string
  bankAccountId: number
  bankAccountLabel: string
}

const COUNTERPART_LABELS: Record<string, { label: string; icon: typeof FileText }> = {
  mirage_open_item: { label: 'Open Item', icon: FileText },
  gl_account_post: { label: 'Direct posting', icon: BookOpen },
  bank_account_transfer: { label: 'Transfer', icon: ArrowLeftRight },
}

const STATE_CONFIG: Record<MatchState, { badge: string; badgeClass: string }> = {
  committed: { badge: 'Committed', badgeClass: 'bg-blue-100 text-blue-700 border-blue-200' },
  confirmed: { badge: 'Confirmed', badgeClass: 'bg-green-100 text-green-700 border-green-200' },
  auto_confirmed: { badge: 'Auto-confirmed', badgeClass: 'bg-neutral-100 text-neutral-600 border-neutral-200' },
  suggested: { badge: 'AI Suggested', badgeClass: 'bg-amber-100 text-amber-700 border-amber-200' },
  unmatched: { badge: 'Unmatched', badgeClass: 'bg-red-100 text-red-600 border-red-200' },
  reversed: { badge: 'Reversed', badgeClass: 'bg-purple-100 text-purple-700 border-purple-200' },
  rejected: { badge: 'Rejected', badgeClass: 'bg-neutral-100 text-neutral-500 border-neutral-200' },
}


export function TransactionRowExpanded({
  transaction,
  clientId,
  bankAccountId,
  bankAccountLabel,
}: TransactionRowExpandedProps) {
  const state: MatchState = transaction.match?.state ?? 'unmatched'
  const confirm = useConfirmMatch()
  const reverse = useReverseMatch()
  const commit = useCommitMatches()
  const [hardDelete, setHardDelete] = useState(false)
  const [commitDialogOpen, setCommitDialogOpen] = useState(false)

  const matchId = transaction.match?.id
  const hasMatch = !!transaction.match && transaction.match.counterparts.length > 0
  // Whether reversing this match should leave Mirage untouched. True when every
  // counterpart points at a Mirage row that pre-existed (link/pre-match) — we
  // shouldn't storno entries we didn't create.
  const allPreExisting =
    !!transaction.match &&
    transaction.match.counterparts.length > 0 &&
    transaction.match.counterparts.every((c) => c.mirage_pre_existing === true)

  const isResolved = state === 'committed' || state === 'confirmed'
  const isAiMatch = state === 'suggested' || state === 'auto_confirmed'

  return (
    <div
      className={cn(
        'border-t px-12 py-4',
        isResolved && 'border-l-2 border-l-green-400 bg-green-50/40',
        isAiMatch && 'border-l-2 border-l-amber-400 bg-amber-50/30',
        state === 'unmatched' && 'bg-neutral-50/50',
        state === 'reversed' && 'bg-purple-50/30',
        state === 'rejected' && 'bg-neutral-50',
        !isResolved && !isAiMatch && state !== 'unmatched' && state !== 'reversed' && state !== 'rejected' && 'bg-neutral-50'
      )}
    >
      {/* Header row: details + status badge */}
      <div className="mb-3 flex items-start justify-between gap-4">
        <div className="flex items-center gap-6 text-sm">
          <Detail label="Reference" value={transaction.reference || '—'} />
          {transaction.balance_after != null && (
            <Detail
              label="Balance after"
              value={formatCurrency(transaction.balance_after, transaction.currency)}
              mono
            />
          )}
          {transaction.match?.agent_confidence != null && (
            <Detail
              label="AI confidence"
              value={`${Math.round(transaction.match.agent_confidence * 100)}%`}
            />
          )}
        </div>
        <Badge className={cn('shrink-0', STATE_CONFIG[state].badgeClass)}>
          {STATE_CONFIG[state].badge}
        </Badge>
      </div>

      {/* AI reasoning */}
      {transaction.match?.agent_reasoning && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50/60 px-3 py-2 text-[13px] text-blue-800">
          <Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-500" />
          <span>{transaction.match.agent_reasoning}</span>
        </div>
      )}

      {/* Matched counterparts + journal preview */}
      {hasMatch && (
        <>
          {/* Editable for non-committed/non-reversed; read-only for committed and reversed (historical audit) */}
          {state !== 'committed' && state !== 'reversed' ? (
            <div className="mb-3">
              <CounterpartEditor
                match={transaction.match!}
                transaction={transaction}
                clientId={clientId}
                bankAccountId={bankAccountId}
                bankAccountLabel={bankAccountLabel}
              />
            </div>
          ) : (
            <div className="mb-3 rounded-md border border-neutral-200 bg-white">
              <div className="flex items-center gap-1.5 border-b border-neutral-200 px-3 py-1.5">
                <FileText className="h-3.5 w-3.5 text-neutral-400" />
                <span className="text-[11px] font-medium uppercase tracking-wide text-neutral-400">
                  Matched to
                </span>
              </div>
              <div className="divide-y divide-neutral-100">
                {transaction.match!.counterparts.map((cp) => {
                  const kindConfig = COUNTERPART_LABELS[cp.counterpart_kind] ?? { label: 'Linked entry', icon: Link2 }
                  const amount = parseFloat(cp.amount)
                  const apxFields: APXFieldValue[] | undefined =
                    cp.dt_account_id !== bankAccountId
                      ? cp.dt_apx_fields
                      : cp.ct_apx_fields

                  return (
                    <div key={cp.id} className="px-3 py-1.5">
                      <div className="flex items-center gap-3 text-sm">
                        <span className="shrink-0 text-neutral-500">
                          {kindConfig.label}
                        </span>
                        {cp.doc_nom && (
                          <span className="shrink-0 font-medium text-neutral-900">
                            #{cp.doc_nom}
                          </span>
                        )}
                        {cp.doc_date && (
                          <span className="shrink-0 text-neutral-400">
                            {cp.doc_date}
                          </span>
                        )}
                        <span className="min-w-0 flex-1 truncate text-neutral-400">
                          {cp.note || ''}
                        </span>
                        <span
                          className={cn(
                            'shrink-0 font-mono font-medium',
                            amount >= 0 ? 'text-green-700' : 'text-red-600'
                          )}
                        >
                          {formatCurrency(cp.amount, transaction.currency)}
                        </span>
                      </div>
                      {apxFields && apxFields.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 pl-0 text-xs text-neutral-500">
                          {apxFields.map((f) => (
                            <span key={f.field_name}>
                              <span className="text-neutral-400">{f.label}:</span>{' '}
                              <span className="text-neutral-600">{f.value}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

        </>
      )}

      {/* Action tabs for unmatched and reversed transactions */}
      {(state === 'unmatched' || state === 'reversed') && (
        <Tabs defaultValue="match">
          <TabsList className="mb-0">
            <TabsTrigger value="match" className="gap-1.5">
              <Search className="h-3.5 w-3.5" />
              Match
            </TabsTrigger>
            <TabsTrigger value="post" className="gap-1.5">
              <BookOpen className="h-3.5 w-3.5" />
              Post
            </TabsTrigger>
            <TabsTrigger value="split" className="gap-1.5">
              <Scissors className="h-3.5 w-3.5" />
              Split
            </TabsTrigger>
            <TabsTrigger value="transfer" className="gap-1.5">
              <ArrowLeftRight className="h-3.5 w-3.5" />
              Transfer
            </TabsTrigger>
            <TabsTrigger value="link" className="gap-1.5">
              <Link2 className="h-3.5 w-3.5" />
              Link existing
            </TabsTrigger>
          </TabsList>
          <TabsContent value="match" className="rounded-b-md border border-t-0 bg-white p-4">
            <MatchTab transaction={transaction} clientId={clientId} bankAccountId={bankAccountId} bankAccountLabel={bankAccountLabel} />
          </TabsContent>
          <TabsContent value="post" className="rounded-b-md border border-t-0 bg-white p-4">
            <PostTab transaction={transaction} clientId={clientId} bankAccountId={bankAccountId} bankAccountLabel={bankAccountLabel} />
          </TabsContent>
          <TabsContent value="split" className="rounded-b-md border border-t-0 bg-white p-4">
            <SplitTab transaction={transaction} clientId={clientId} bankAccountId={bankAccountId} bankAccountLabel={bankAccountLabel} />
          </TabsContent>
          <TabsContent value="transfer" className="rounded-b-md border border-t-0 bg-white p-4">
            <TransferTab transaction={transaction} clientId={clientId} bankAccountId={bankAccountId} bankAccountLabel={bankAccountLabel} />
          </TabsContent>
          <TabsContent value="link" className="rounded-b-md border border-t-0 bg-white p-4">
            <LinkTab transaction={transaction} clientId={clientId} bankAccountId={bankAccountId} bankAccountLabel={bankAccountLabel} />
          </TabsContent>
        </Tabs>
      )}

      {/* Action buttons */}
      {state !== 'unmatched' && state !== 'reversed' && (
        <div className="flex items-center gap-2 pt-1">
          {/* Suggested / Auto-confirmed: confirm or skip */}
          {isAiMatch && matchId && (
            <>
              <Button
                size="sm"
                variant="blue"
                className="gap-1.5"
                disabled={confirm.isLoading}
                onClick={() => confirm.mutate(matchId)}
              >
                <Check className="h-3.5 w-3.5" />
                {confirm.isLoading ? 'Confirming...' : 'Confirm match'}
              </Button>
              <Button
                size="sm"
                variant="destructive"
                className="gap-1.5"
                disabled={reverse.isLoading}
                onClick={() => reverse.mutate({ id: matchId, client_id: clientId, hard_delete: true })}
              >
                <X className="h-3.5 w-3.5" />
                {reverse.isLoading ? 'Removing...' : 'Unmatch'}
              </Button>
            </>
          )}

          {/* Confirmed: commit or revert */}
          {state === 'confirmed' && matchId && (
            <>
              <Button
                size="sm"
                variant="blue"
                className="gap-1.5"
                disabled={commit.isLoading}
                onClick={() => setCommitDialogOpen(true)}
              >
                <Check className="h-3.5 w-3.5" />
                {commit.isLoading ? 'Committing...' : 'Commit'}
              </Button>
              <CommitDialog
                open={commitDialogOpen}
                onOpenChange={setCommitDialogOpen}
                transactions={[transaction]}
                clientId={clientId}
                loading={commit.isLoading}
                triggerLabel="Commit"
                onConfirm={(overrides) =>
                  commit.mutate(
                    { match_ids: [matchId], client_id: clientId, ...overrides },
                    { onSuccess: () => setCommitDialogOpen(false) },
                  )
                }
              />

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="gap-1.5"
                    disabled={reverse.isLoading}
                  >
                    <Undo2 className="h-3.5 w-3.5" />
                    {reverse.isLoading ? 'Reverting...' : 'Revert match'}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Revert this match?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will remove the match and return the transaction to unmatched state. You can re-match it afterwards.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-neutral-900 text-white hover:bg-neutral-800"
                      onClick={() => reverse.mutate({ id: matchId, client_id: clientId, hard_delete: true })}
                    >
                      Revert
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}

          {/* Committed: unlink (pre-existing) or storno (we created it) */}
          {state === 'committed' && matchId && (
            <AlertDialog onOpenChange={(open) => { if (!open) setHardDelete(false) }}>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="destructive"
                  className="gap-1.5"
                  disabled={reverse.isLoading}
                >
                  {allPreExisting ? <Undo2 className="h-3.5 w-3.5" /> : <RotateCcw className="h-3.5 w-3.5" />}
                  {reverse.isLoading
                    ? (allPreExisting ? 'Unlinking...' : 'Reversing...')
                    : (allPreExisting ? 'Unlink' : 'Reverse')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {allPreExisting ? 'Unlink this entry?' : 'Reverse committed entry?'}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {allPreExisting
                      ? 'This removes the link in our system. The journal entry in Mirage stays as-is — you didn\'t create it through this app.'
                      : 'This will reverse the journal entry in Mirage ERP with a storno record. This action is auditable.'}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                {!allPreExisting && (
                  <label className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-sm text-amber-900">
                    <Checkbox
                      checked={hardDelete}
                      onCheckedChange={(v) => setHardDelete(v === true)}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="font-medium">Hard delete instead of storno</span>
                      <span className="block text-xs text-amber-800">
                        Removes the entry entirely instead of leaving an audit-trail storno. Only use when no downstream documents reference it — irreversible.
                      </span>
                    </span>
                  </label>
                )}
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className={cn(
                      'text-white',
                      allPreExisting ? 'bg-neutral-900 hover:bg-neutral-800' : 'bg-red-600 hover:bg-red-700'
                    )}
                    onClick={() =>
                      reverse.mutate({
                        id: matchId,
                        client_id: clientId,
                        hard_delete: allPreExisting ? false : hardDelete,
                      })
                    }
                  >
                    {allPreExisting ? 'Unlink' : (hardDelete ? 'Hard delete' : 'Reverse')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          {/* Terminal states */}
          {state === 'rejected' && (
            <span className="text-sm text-neutral-500">Suggestion rejected</span>
          )}
        </div>
      )}
    </div>
  )
}

function Detail({
  label,
  value,
  mono,
}: {
  label: string
  value: string
  mono?: boolean
}) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-neutral-400">{label}:</span>
      <span className={cn('font-medium text-neutral-700', mono && 'font-mono')}>
        {value}
      </span>
    </div>
  )
}
